/**
 * Localization Context - Provides localization utilities throughout the app
 *
 * Usage:
 * const { formatCurrency, formatWeight, getCurrencySymbol, currency } = useLocalization();
 *
 * NOTE: Prices are always USD, returned directly by the API - no currency conversion needed.
 */

import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import LocalizationService, {type  Currency, type WeightUnit } from '../utils/localizationService';

interface LocalizationContextValue {
  // Currency formatting
  formatCurrency: (amount: number, convertCurrency?: boolean) => string;
  formatLocalCurrency: (amount: number) => string;
  getCurrencySymbol: () => string;
  convertFromUSD: (amountUSD: number) => number;

  // Weight formatting
  formatWeight: (weightLbs: number) => {
    display: string;
    value: number;
    unit: WeightUnit;
    mainValue: string;
    secondaryValue: string;
  };
  formatWeightWithBothUnits: (weightLbs: number) => string;
  formatWeightRange: (minLbs: number, maxLbs: number) => { min: number; max: number; unit: WeightUnit };
  convertWeight: (value: number, fromUnit: WeightUnit, toUnit: WeightUnit) => number;
  getWeightUnit: () => WeightUnit;
  lbsToKg: (lbs: number) => number;
  kgToLbs: (kg: number) => number;
  convertLbsToKg: (lbs: number) => number;
  calculatePricePerLb: (price: number, weightLbs: number) => number | null;
  formatPricePerLb: (price: number, weightLbs: number) => string | null;

  // Date formatting
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  getDateFormat: () => string;
  getTimeFormat: () => '12h' | '24h';
  formatDateForInput: (date: Date | null) => string;
  formatTimeForInput: (hour24: number, minute: number) => string;
  getDatePlaceholder: () => string;
  getTimePlaceholder: () => string;

  // Configuration
  currency: Currency;
  locale: string;
  timeZone: string;
  isMetric: boolean;
}

const LocalizationContext = createContext<LocalizationContextValue | undefined>(undefined);

interface LocalizationProviderProps {
  children: ReactNode;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const service = LocalizationService.getInstance();
  const config = service.getConfig();

  // Prices are always USD, returned directly by the API — no currency preference to track.
  const value: LocalizationContextValue = useMemo(() => ({
    // Currency methods
    formatCurrency: (amount: number, convertCurrency?: boolean) => service.formatCurrency(amount, convertCurrency),
    formatLocalCurrency: (amount: number) => service.formatLocalCurrency(amount),
    getCurrencySymbol: () => service.getCurrencySymbol(),
    convertFromUSD: (amountUSD: number) => service.convertFromUSD(amountUSD),

    // Weight methods
    formatWeight: (weightLbs: number) => service.formatWeight(weightLbs),
    formatWeightWithBothUnits: (weightLbs: number) => service.formatWeightWithBothUnits(weightLbs),
    formatWeightRange: (minLbs: number, maxLbs: number) => service.formatWeightRange(minLbs, maxLbs),
    convertWeight: (value: number, fromUnit: WeightUnit, toUnit: WeightUnit) =>
      service.convertWeight(value, fromUnit, toUnit),
    getWeightUnit: () => service.getWeightUnit(),
    lbsToKg: (lbs: number) => service.lbsToKg(lbs),
    kgToLbs: (kg: number) => service.kgToLbs(kg),
    convertLbsToKg: (lbs: number) => service.convertLbsToKg(lbs),
    calculatePricePerLb: (price: number, weightLbs: number) => service.calculatePricePerLb(price, weightLbs),
    formatPricePerLb: (price: number, weightLbs: number) => service.formatPricePerLb(price, weightLbs),

    // Date methods
    formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) =>
      service.formatDate(date, options),
    formatDateTime: (date: Date | string, options?: Intl.DateTimeFormatOptions) =>
      service.formatDateTime(date, options),
    getDateFormat: () => service.getDateFormat(),
    getTimeFormat: () => service.getTimeFormat(),
    formatDateForInput: (date: Date | null) => service.formatDateForInput(date),
    formatTimeForInput: (hour24: number, minute: number) => service.formatTimeForInput(hour24, minute),
    getDatePlaceholder: () => service.getDatePlaceholder(),
    getTimePlaceholder: () => service.getTimePlaceholder(),

    // Configuration
    currency: config.currency,
    locale: config.locale,
    timeZone: config.timeZone,
    isMetric: config.isMetric,
  }), [service, config]);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

/**
 * Hook to access localization utilities
 *
 * @example
 * const { formatCurrency, formatWeight, getCurrencySymbol } = useLocalization();
 *
 * // Display price (already in user's currency from backend)
 * <p>{formatCurrency(item.price)}</p>
 *
 * // Display weight (convert from lbs to local unit)
 * <p>{formatWeight(item.weightLbs).display}</p>
 *
 * // Get currency symbol
 * const symbol = getCurrencySymbol(); // Returns '$', '€', '£', etc.
 */
export const useLocalization = (): LocalizationContextValue => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider');
  }
  return context;
};

export default LocalizationContext;
